import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus } from "@phosphor-icons/react";

const TYPES = [
  { value: "PLANNED", label: "Planned" },
  { value: "EMERGENCY", label: "Emergency" },
  { value: "MEDICAL", label: "Medical" },
  { value: "WELLNESS", label: "Women's Wellness" },
  { value: "UNPAID", label: "Unpaid" },
];

export default function StaffLeave() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    leave_type: "PLANNED",
    from_date: new Date().toISOString().slice(0, 10),
    to_date: new Date().toISOString().slice(0, 10),
    reason: "",
  });

  async function load() {
    const { data } = await api.get("/leave?mine=true");
    setRows(data);
  }
  useEffect(() => { load(); }, []);

  async function submit() {
    try {
      await api.post("/leave", form);
      toast.success("Leave applied");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div className="px-5 pt-6 pb-12 max-w-md mx-auto">
      <div className="flex items-end justify-between mb-4">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#E50914]">Time-off</p>
          <h1 className="font-display text-3xl tracking-tight">My Leave</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#E50914] hover:bg-[#C80812]" data-testid="staff-apply-leave-button">
              <Plus size={16} className="mr-1" /> Apply
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#16161C] border-[#2D2D35] text-white">
            <DialogHeader><DialogTitle>Apply for leave</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Type</Label>
                <Select value={form.leave_type} onValueChange={(v) => setForm({...form, leave_type: v})}>
                  <SelectTrigger className="mt-2 bg-[#0D0D10] border-[#2D2D35]" data-testid="leave-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#16161C] border-[#2D2D35] text-white">
                    {TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs uppercase tracking-widest text-zinc-500">From</Label>
                  <Input type="date" value={form.from_date} onChange={(e)=>setForm({...form, from_date: e.target.value})}
                         className="mt-2 bg-[#0D0D10] border-[#2D2D35] [color-scheme:dark]" data-testid="leave-from" /></div>
                <div><Label className="text-xs uppercase tracking-widest text-zinc-500">To</Label>
                  <Input type="date" value={form.to_date} onChange={(e)=>setForm({...form, to_date: e.target.value})}
                         className="mt-2 bg-[#0D0D10] border-[#2D2D35] [color-scheme:dark]" data-testid="leave-to" /></div>
              </div>
              <div><Label className="text-xs uppercase tracking-widest text-zinc-500">Reason</Label>
                <Textarea value={form.reason} onChange={(e)=>setForm({...form, reason: e.target.value})}
                          className="mt-2 bg-[#0D0D10] border-[#2D2D35]" data-testid="leave-reason" /></div>
              <Button onClick={submit} className="w-full bg-[#E50914] hover:bg-[#C80812]" data-testid="submit-leave-button">Submit</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="space-y-3">
        {rows.map((r) => (
          <div key={r.id} className="bg-[#16161C] border border-[#2D2D35] rounded-md p-4">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-sm font-medium">{r.leave_type}</div>
                <div className="text-xs text-zinc-400 font-mono mt-1">{r.from_date} → {r.to_date}</div>
              </div>
              <Status status={r.status} />
            </div>
            <div className="text-xs text-zinc-400 mt-2">{r.reason}</div>
          </div>
        ))}
        {!rows.length && <div className="text-sm text-zinc-500 text-center py-8">No leave history.</div>}
      </div>
    </div>
  );
}

function Status({ status }) {
  const map = {
    APPROVED: "bg-[#22C55E]/15 text-[#22C55E]",
    REJECTED: "bg-[#FF3B30]/15 text-[#FF3B30]",
    PENDING: "bg-[#F59E0B]/15 text-[#F59E0B]",
  };
  return <span className={`text-[10px] px-2 py-1 rounded ${map[status] || "bg-zinc-700/30 text-zinc-300"}`}>{status}</span>;
}
